import { Link } from "react-router-dom";
import { CalendarCheck, CheckCircle2, ClipboardCheck, Link2, ShieldCheck } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

type MethodologyItem = {
  title: string;
  body: string;
};

type ComparisonMethodologyProps = {
  title?: string;
  description?: string;
  items?: MethodologyItem[];
};

const defaultItems: MethodologyItem[] = [
  {
    title: "Villkor före provision",
    body: "Vi prioriterar ränta, avgifter, lånebelopp, krav, svarstid och tydlighet före affiliateersättning.",
  },
  {
    title: "Data behöver hållas färsk",
    body: "Räntor, avgifter och krav ändras. Jämförelsesidor ska därför flaggas för uppdatering när partnerdata kan vara gammal.",
  },
  {
    title: "Internlänkar ska hjälpa användaren",
    body: "Guider länkar till relevanta jämförelser och jämförelser länkar tillbaka till förklarande guider.",
  },
];

export const ComparisonMethodology = ({
  title = "Så jämför vi och håller sidan uppdaterad",
  description = "Ekonomiska jämförelser behöver vara lätta att förstå och möjliga att kontrollera. Den här modellen används för lån, bolån, företagslån och lån utan UC.",
  items = defaultItems,
}: ComparisonMethodologyProps) => {
  const { currentLanguage } = useLanguage();

  const icons = [ClipboardCheck, CalendarCheck, Link2];

  return (
    <section className="border-y border-border/60 bg-muted/30 py-8 md:py-12">
      <div className="container max-w-6xl">
        <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
              <ShieldCheck className="h-4 w-4" />
              Metodik
            </div>
            <h2 className="text-2xl font-bold tracking-tight md:text-3xl">{title}</h2>
            <p className="text-muted-foreground">{description}</p>
            <Link to={`/${currentLanguage}/redaktionen`} className="inline-flex text-sm font-medium text-primary hover:underline">
              Läs mer om redaktionell kontroll
            </Link>
          </div>

          <div className="grid gap-3">
            {items.map((item, index) => {
              const Icon = icons[index % icons.length] || CheckCircle2;
              return (
                <div key={item.title} className="flex gap-3 rounded-lg border border-border/70 bg-background p-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{item.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{item.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
